import React, {useMemo, useState} from 'react';
import Chip from '@mui/material/Chip';
import type {PublisherNameVariantFieldLayout} from 'components/fields/types';
import type {
  FieldError,
  NameVariantValue,
  PartNameVariant,
} from 'schemaDefinition/types';
import {assertAsNameVariantValue} from 'schemaDefinition/functions';
import {toFieldError} from 'schemaDefinition/functions/validators';
import {FieldLayout, Layout, TextField} from 'components';
import type {BaseFieldProps} from './types';
import {useCustomValidator, useShowFieldErrors} from '../../hooks';

type Props = BaseFieldProps & {
  part: PartNameVariant;
  isMainForm: boolean;
  layout: PublisherNameVariantFieldLayout;
};

const makeNameVariantValue = (
  id: string | null | undefined,
  name: string | null | undefined,
): NameVariantValue | null => {
  if (![name].some(v => typeof v === 'string')) {
    return null;
  }

  const value: NameVariantValue = {};

  if (id) {
    value.id = id;
  }

  if (typeof name === 'string') {
    value.name = name;
  }
  return value;
};

export const PublisherNameVariant: React.FC<Props> = ({
  part,
  value,
  setFieldValue,
  isMainForm,
  valuePath,
  mode,
  showWhenReadonlyAndEmpty,
  focusableId,
  layout,
}) => {
  const {id, name} = useMemo(() => {
    const nameVariantValue = assertAsNameVariantValue(value, valuePath);
    return {
      id: null,
      name: null,
      ...nameVariantValue,
    };
  }, [value, valuePath]);
  const validate = useCustomValidator(part);
  const [visited, setVisited] = useState<{name?: boolean}>({});
  const showFieldErrors = useShowFieldErrors(visited.name ?? false);
  const error = useMemo((): FieldError => {
    if (showFieldErrors) {
      const valid = validate ? validate(part, value) : 'valid';
      return toFieldError(valid);
    }

    return false;
  }, [part, showFieldErrors, validate, value]);

  return (
    <Layout horizontal>
      <FieldLayout horizontal>
        <TextField
          type="text"
          name="name"
          focusableId={focusableId}
          placeholder={layout.name.placeholder}
          readonly={mode === 'read-only'}
          showWhenReadonlyAndEmpty={showWhenReadonlyAndEmpty}
          value={name}
          layout={layout.name.layout}
          onChange={e => {
            const newValue = e.target.value;
            setFieldValue(valuePath, makeNameVariantValue(id, newValue));
          }}
          onBlur={() => setVisited(v => ({...v, name: true}))}
          error={error}
        />

        <Layout hidden={!isMainForm} {...layout.mainForm.layout} adjustCenter>
          <Chip
            size="small"
            label={'Hovedform'}
            sx={{background: '#FFEEB1'}} // was ColorPalette.spot.urgent
          />
        </Layout>
      </FieldLayout>
    </Layout>
  );
};
